<template>
  <div class="order-book">
    <div class="title">
      <h4>{{ $t("trading.orderbook.title") }}</h4>
    </div>
    <div class="gray-line"></div>
    <div class="header">
      <div class="col price">{{ $t("trading.table.price") }} (USDT)</div>
      <div class="col">{{ $t("trading.table.size") }} ({{ ticker }})</div>
      <div class="col">{{ $t("trading.orderbook.sum") }} ({{ ticker }})</div>
    </div>
    <Spinner v-if="isSocketConnecting || isDepthConnecting"/>
    <div class="asks" v-if="!isSocketConnecting && !isDepthConnecting">
      <div v-for="(ask, index) in reversedAsks" :key="'ask-' + index" class="row" @click="checkOrderbookPrice(ask.price)">
        <div
          class="depth-bar red"
          :style="{ width: getDepthWidth(ask.sum, maxSum, 'ask') }" 
        />
        <div class="col price red">{{ ask.price }}</div>
        <div class="col">{{ formatQuantity(ask.amount) }}</div>
        <div class="col">{{ formatQuantity(ask.sum) }}</div>
      </div>
    </div>
    <div class="current-price" v-if="!isSocketConnecting && !isDepthConnecting">{{ currentPrice }}</div>

    <div class="bids" v-if="!isSocketConnecting && !isDepthConnecting">
      <div v-for="(bid, index) in sortedBids" :key="'bid-' + index" class="row" @click="checkOrderbookPrice(bid.price)">
        <div
          class="depth-bar green"
          :style="{ width: getDepthWidth(bid.sum, maxSum, 'bid') }"
        />
        <div class="col price green">{{ bid.price }}</div>
        <div class="col">{{ formatQuantity(bid.amount) }}</div>
        <div class="col">{{ formatQuantity(bid.sum) }}</div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import { useStore } from "vuex";
import { throttle } from "lodash-es";
import Spinner from "../common/spinner/Spinner.vue";
const store = useStore();
const currentPrice = computed(() => store.state.transact.lastPrice);
const ticker = computed(() => store.state.transact.ticker);
const rawOrderbook = computed(
  () => store.state.transact.orderbookInfo || { bids: [], asks: [] }
);
const isSocketConnecting = computed(() => store.state.transact.isSocketConnecting);
const isDepthConnecting = computed(() => store.state.transact.isDepthConnecting);

const displayOrderbook = ref({ bids: [], asks: [] });

const addCumulativeSum = (orders) => {
  let sum = 0;
  return orders.map((order) => {
    sum += order.amount;
    return { ...order, sum: Number(sum.toFixed(3)) };
  });
};

const sortedAsks = computed(() => {
  return addCumulativeSum(
    [...displayOrderbook.value.asks].sort((a, b) => a.price - b.price)
  );
});
const reversedAsks = computed(() => [...sortedAsks.value].reverse());
const sortedBids = computed(() => {
  return addCumulativeSum(
    [...displayOrderbook.value.bids].sort((a, b) => b.price - a.price)
  );
});
const maxSum = computed(() => {
  const askMax = Math.max(...sortedAsks.value.map((a) => a.sum), 0);
  const bidMax = Math.max(...sortedBids.value.map((b) => b.sum), 0);
  return Math.max(askMax, bidMax);
});
const bidMaxSum = computed(() => Math.max(...sortedBids.value.map((b) => b.sum), 0));
const askMaxSum = computed(() => Math.max(...sortedAsks.value.map((a) => a.sum), 0));

const throttledUpdateOrderbook = throttle(() => {
  displayOrderbook.value = {
    bids: rawOrderbook.value.bids.slice(),
    asks: rawOrderbook.value.asks.slice(),
  };
}, 500);

const getDepthWidth = (sum, max) => {
  const percent = max === 0 ? 0 : (sum / max) * 100;
  return `${percent}%`;
};

const formatQuantity = (value) => {
  if (value < 1_000) return value;
  if (value >= 1_000_000_000)
    return Math.floor((value / 1_000_000_000) * 100) / 100 + "B";
  if (value >= 1_000_000) return Math.floor((value / 1_000_000) * 100) / 100 + "M";
  if (value >= 1_000) return Math.floor((value / 1_000) * 100) / 100 + "K";
  return Math.floor(value * 100) / 100;
};
const checkOrderbookPrice = (v) => {
  return;
  // store.commit("transact/setOrderbookPrice",v);
}
watch(
  rawOrderbook,
  () => {
    throttledUpdateOrderbook();
  },
  { deep: true }
);
</script>

<style lang="scss" scoped>
.order-book {
  width: 250px;
  height: 700px;
  font-family: sans-serif;
  font-size: 12px;
  color: var(--txt-default-color);
  background: #0b0e11;
  padding: 10px;
  border-radius: 6px;
}

.title {
  h4 {
    text-align: center;
    font-size: 2rem;
  }
}

.header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 6px;
  color: var(--tex-gray-color);
}

.col {
  width: 33%;
  text-align: right;
  position: relative;
  z-index: 1;

  &.price {
    text-align: left;
  }
}

.row {
  position: relative;
  display: flex;
  justify-content: space-between;
  height: 22px;
  line-height: 20px;
  overflow: hidden;
  padding: 0 4px;
}

.depth-bar {
  position: absolute;
  top: 0;
  bottom: 0;
  opacity: 0.2;
  z-index: 0;
}

.depth-bar.red {
  background: #f6465d;
  right: 0;
}

.depth-bar.green {
  background: #0ecb81;
  right: 0;
}

.red {
  color: #f6465d;
}

.green {
  color: #0ecb81;
}

.current-price {
  font-size: 20px;
  text-align: left;
  color: #0ecb81;
  font-weight: bold;
  margin: 8px 0;
}
</style>
